// src/components/ui/responsive-sheet.tsx
import { useIsTouchDevice, useOrientation } from "@/hooks/useResponsive";
import { cn } from "@u/cn";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@ui/drawer";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@ui/sheet";
import { useTranslation } from "react-i18next";

interface ResponsiveSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  titleNode?: React.ReactNode;
  /** optional leading icon shown before the title (e.g. <FlaskConical size={14} />) */
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  variant?: "default" | "glass";
  handleOnly?: boolean;
  hideTitle?: boolean;
}

export default function ResponsiveSheet({
  open,
  onOpenChange,
  title,
  children,
  className,
  variant,
  titleNode,
  icon,
  handleOnly,
  hideTitle,
}: ResponsiveSheetProps) {
  const { t } = useTranslation();
  const isTouchDevice = useIsTouchDevice();
  const { isLandscape } = useOrientation();

  // REGLA: Móvil Vertical = Drawer. Móvil Horizontal o Desktop = Sheet.
  const showDrawer = isTouchDevice && !isLandscape;

  if (showDrawer) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} handleOnly={handleOnly}>
        <DrawerContent
          className={cn(
            "px-5 pb-safe-8 flex flex-col max-h-[85dvh] outline-none",
            variant === "glass" && "glass-panel",
            className,
          )}
        >
          <DrawerDescription className="sr-only">
            {title ?? t("options_menu")}
          </DrawerDescription>

          <DrawerHeader className="mb-2 px-0 shrink-0">
            {!hideTitle && (
              <DrawerTitle className="flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
                {icon}
                {titleNode ?? title}
              </DrawerTitle>
            )}
          </DrawerHeader>

          <div className="flex flex-col gap-2 overflow-y-auto [&>*]:shrink-0 flex-1 pb-2 outline-none">
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // REGLA: Landscape o Fallback -> Sheet Right
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          // px-5 = mismo padding que el drawer portrait, así los full-bleed
          // (-mx-5 de Health/NotificationPanel) alinean en ambas superficies.
          "rounded-l-3xl px-5 pr-safe-4 pb-2 pt-2 outline-none",
          variant === "glass" && "glass-panel",
          className,
        )}
      >
        <SheetDescription className="sr-only">
          {title ?? "Menú de opciones"}
        </SheetDescription>

        <SheetHeader showClose onClose={() => onOpenChange(false)}>
          {!hideTitle && (
            <SheetTitle className="flex items-center gap-2 text-sm uppercase tracking-widest text-muted-foreground">
              {icon}
              {titleNode ?? title}
            </SheetTitle>
          )}
        </SheetHeader>
        <div className="flex flex-col gap-2 overflow-y-auto [&>*]:shrink-0 flex-1 pb-2 outline-none">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
